import React, { SyntheticEvent, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import {
  resetTransitionData,
  setNeedShowAteAction,
} from 'store/actions/transitData';
import {
  resetFoundLayerAction,
  setCurrentLayerAction,
} from 'store/actions/layers';
import {
  getStatDataAction,
  getStatDatasetSelectAction,
  langUpdateStatAteLayerAction,
  needUpdateAction,
  resetStatDataAction,
  resetStatSelectsAction,
  setBackdropLoadingAction,
  setCurrentStatAction,
  setEmptyStatisticsDataAction,
  setStatisticsErrorMsgAction,
  updateStatDataAction,
} from 'store/actions/mainPage';
import { getText } from 'services/languageService';
import { TooltipMapIcon } from 'components/UI/TooltipMapIcon';
import { PieStatAte } from 'components/UI/StatisticsSelects/PieStatAte';
import { InfoModal } from 'components/UI/InfoModal';
import { YearSelect } from 'components/UI/YearSelect';
import {
  checkAte,
  checkTransitData,
  getAteForBuilding,
  getPieRequirements,
  getStatQueryParams,
  resetWmtsLayer,
  setDefaultLayer,
  TabType,
} from 'components/UI/StatisticsSelects/constants';
import { StatAteLayers } from 'components/UI/StatisticsSelects/StatAteLayers';
import {
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteValue,
  Box,
  Button,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { Effects } from './Effects';

export const StatisticsSelects: React.FC<{ tabType: TabType }> = ({
  tabType,
}) => {
  const {
    isLogged,
    currentLayer,
    currentDataset,
    statisticsDatasets,
    statisticsLayers,
    statisticsData,
    currentAteRegions,
    currentAteCities,
    currentAteVC,
    needUpdate,
    statisticsErrorMsg,
    emptyStatisticsData,
  } = useAppSelector((state) => state.mainPage);
  const {
    ateRegions,
    currentYear,
    years,
    currentLayerId: wmtsLayer,
  } = useAppSelector((state) => state.layers);
  const transitData = useAppSelector((state) => state.transitData);
  const dispatch = useAppDispatch();
  const [openInfo, setOpenInfo] = useState<boolean>(false);
  const [textInfo, setTextInfo] = useState<string>('');

  const { layer, dataset, ateRegion, ateCity, ateVC, from } = transitData;
  const { all, onlyLayer, onlyAte } = checkTransitData(
    layer,
    dataset,
    ateRegion,
    ateCity,
    ateVC
  );

  const checkedAte = checkAte(
    currentAteVC,
    currentAteCities,
    currentAteRegions
  );

  const showError = () => {
    setOpenInfo(true);
    setTextInfo(getText('WRONG_STATISTICS'));
    if (statisticsData) {
      dispatch(resetStatDataAction());
    }
  };

  const handleChangeDs = (
    event: SyntheticEvent<Element, Event>,
    value: AutocompleteValue<any, any, any, any> | null,
    reason: AutocompleteChangeReason
  ) => {
    if (value) {
      dispatch(setCurrentStatAction('currentDataset', value));
      dispatch(setCurrentStatAction('currentLayer', null));
    } else {
      dispatch(resetStatSelectsAction());
    }
  };

  const handleChangeLayer = (
    event: SyntheticEvent<Element, Event>,
    value: AutocompleteValue<any, any, any, any> | null,
    reason: AutocompleteChangeReason
  ) => {
    setDefaultLayer(currentDataset, dispatch);

    if (value) {
      dispatch(setCurrentStatAction('currentLayer', value));
    } else {
      dispatch(setCurrentStatAction('currentLayer', null));
    }
  };

  useEffect(() => {
    const itemAll = ateRegions.find((item) => item.id === 'all');
    if (!statisticsDatasets.length || !itemAll) {
      dispatch(needUpdateAction(true));
    } else {
      dispatch(
        getStatDatasetSelectAction(isLogged, currentYear, tabType, false)
      ).then((res: any[]) => {
        const foundDs = res.find((ds) => ds.id === currentDataset?.id);

        if (!foundDs && currentDataset) {
          dispatch(setCurrentStatAction('currentDataset', null));
          dispatch(setCurrentStatAction('currentLayer', null));
          dispatch(setCurrentStatAction('statisticsLayers', []));
          tabType === 'pie' && showError();
        }
        if (res.length && requirements && foundDs) {
          setTimeout(onBuild, 250);
        } else {
          dispatch(setBackdropLoadingAction(false));
        }
      });
    }

    return () => {
      dispatch(needUpdateAction(false));
      resetWmtsLayer(dispatch);
      dispatch(resetFoundLayerAction());
      dispatch(resetStatDataAction()); // #todo need observe
    };
  }, []);

  //update after lang change and change statistics tab
  useEffect(() => {
    if (!statisticsDatasets.length) return;

    if (currentDataset) {
      const newDs = statisticsDatasets.find(
        (item) => item.id === currentDataset.id
      );
      if (newDs) {
        dispatch(setCurrentStatAction('currentDataset', newDs));
        return;
      }

      if (tabType === 'pie') {
        showError();
        return;
      }
    }

    //if dataset not found
    if (!currentDataset) {
      //!todo remove
      if (onlyLayer || all) {
        const newDs = statisticsDatasets.find((item) => item.id === dataset);
        newDs
          ? dispatch(setCurrentStatAction('currentDataset', newDs))
          : all && dispatch(setNeedShowAteAction(true));
      }
      // else if (!onlyAte) {
      //   const ds = statisticsDatasets[0];
      //   ds && dispatch(setCurrentStatAction('currentDataset', ds));
      //   dispatch(setCurrentStatAction('currentLayer', null));
      // }
    }
  }, [statisticsDatasets]);

  //update after lang change
  useEffect(() => {
    if (currentLayer) {
      const newLayer = statisticsLayers.find(
        (item) => item.id === currentLayer.id
      );
      newLayer && dispatch(setCurrentStatAction('currentLayer', newLayer));
    }
  }, [statisticsLayers]);

  useEffect(() => {
    if (!needUpdate) return;

    const ateParams = getAteForBuilding(
      currentAteRegions,
      currentAteCities,
      currentAteVC
    );
    const params = statisticsData
      ? getStatQueryParams(currentDataset, ateParams, currentLayer, tabType)
      : undefined;

    dispatch(
      langUpdateStatAteLayerAction(
        currentAteRegions,
        currentAteCities,
        currentAteVC
      )
    ).then(() => {
      dispatch(
        updateStatDataAction(isLogged, tabType, params, currentYear)
      ).then((res) => {
        //for set ate if wasn't find layer
        const needTransit = from === 'layers' || from === 'search';

        // for show error on pie tab if dataset doesn't exist for pie
        const foundDs = res.find((ds) => ds.id === dataset);
        if (tabType === 'pie' && needTransit && !foundDs && dataset) {
          showError();
        }

        if (all && needTransit) {
          console.log('need show ate');
          dispatch(setNeedShowAteAction(true));
        }
      });
    });
  }, [needUpdate]);

  useEffect(() => {
    // if (statisticsData) {
    //   dispatch(resetStatDataAction());
    // }
    if (!currentLayer) {
      currentDataset && currentDataset.default_layer
        ? setDefaultLayer(currentDataset, dispatch)
        : resetWmtsLayer(dispatch);
    }
  }, [
    currentLayer,
    currentAteRegions,
    currentAteCities,
    currentAteVC,
    currentDataset,
  ]);

  //show default layer
  useEffect(() => {
    if (!currentDataset) return;

    if (currentDataset.default_layer && !currentLayer) {
      setDefaultLayer(currentDataset, dispatch);
    } else if (!currentDataset || !currentDataset.default_layer) {
      resetWmtsLayer(dispatch);
    } else if (currentLayer) {
      const layer = statisticsLayers.find(
        (item) => item.id === currentLayer.id
      );
      if (layer) {
        dispatch(
          setCurrentLayerAction(
            layer.layer,
            layer.url,
            layer.id,
            layer.legend_file,
            layer.map_file,
            layer.meta_file,
            currentDataset?.name,
            layer.name,
            layer.legend_size,
            layer.scale_1_50,
            layer.scale_1_50_vector
          )
        );
      }
    }
  }, [currentDataset]);

  const handleChangeYear = (event: SelectChangeEvent) => {
    const year = event.target.value;
    dispatch(setCurrentStatAction('currentDataset', null));
    dispatch(setCurrentStatAction('currentLayer', null));
    dispatch(getStatDatasetSelectAction(isLogged, year, tabType));
  };

  useEffect(() => {
    if (!checkedAte && tabType !== 'pie') {
      dispatch(setCurrentStatAction('currentLayer', null));
    }
  }, [currentAteRegions, currentAteCities, currentAteVC]);

  useEffect(() => {
    if (statisticsErrorMsg) {
      setTextInfo(statisticsErrorMsg);
      setOpenInfo(true);
    }
  }, [statisticsErrorMsg]);

  const onBuild = () => {
    if (
      currentLayer &&
      currentLayer.value_type === 'rel' &&
      tabType === 'pie'
    ) {
      showError();
      dispatch(setBackdropLoadingAction(false));
    } else {
      const ateParams = getAteForBuilding(
        currentAteRegions,
        currentAteCities,
        currentAteVC
      );
      const params = getStatQueryParams(
        currentDataset,
        ateParams,
        currentLayer,
        tabType
      );

      if (currentLayer) {
        const layer = statisticsLayers.find(
          (item) => item.id === currentLayer.id
        );
        if (layer) {
          dispatch(
            setCurrentLayerAction(
              layer.layer,
              layer.url,
              layer.id,
              layer.legend_file,
              layer.map_file,
              layer.meta_file,
              currentDataset?.name,
              layer.name,
              layer.legend_size,
              layer.scale_1_50,
              layer.scale_1_50_vector
            )
          );
        }
      }
      // dispatch(resetStatDataAction());
      dispatch(getStatDataAction(isLogged, params));
    }
  };

  const emptyAte =
    currentAteVC.length || currentAteCities.length || currentAteRegions.length;

  const defaultRequirements =
    (currentDataset && currentLayer) ||
    (currentDataset && !checkedAte) ||
    (currentDataset && checkedAte && currentLayer);

  const pieRequirements =
    currentLayer &&
    getPieRequirements(currentAteRegions, currentAteCities, currentAteVC);

  const disableBuild =
    tabType === 'pie' ? !pieRequirements : !emptyAte || !defaultRequirements;

  const requirements = !disableBuild;

  return (
    <>
      <YearSelect
        currentYear={currentYear}
        years={years}
        handleChangeYear={handleChangeYear}
      />

      <Box sx={{ mb: 2, p: '8px 16px 0 16px' }}>
        <Autocomplete
          value={currentDataset}
          size="small"
          fullWidth
          disableListWrap
          options={statisticsDatasets}
          onChange={handleChangeDs}
          renderOption={(props, option) => {
            return (
              <li {...props} key={option.id}>
                {option.name}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField {...params} label={getText('DATASET_SELECT')} />
          )}
        />

        {tabType === 'pie' ? (
          <PieStatAte tabType={tabType} showError={showError} />
        ) : (
          <StatAteLayers tabType={tabType} />
        )}

        <TooltipMapIcon
          placement="right"
          title={!checkedAte ? getText('DIS_STAT_LAYER_TL') : ''}
        >
          <Autocomplete
            value={currentLayer}
            size="small"
            fullWidth
            disableListWrap
            disabled={!checkedAte}
            options={statisticsLayers}
            onChange={handleChangeLayer}
            renderOption={(props, option) => {
              return (
                <li {...props} key={option.id}>
                  {option.name}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField {...params} label={getText('LAYER_SELECT')} />
            )}
          />
        </TooltipMapIcon>
        <Button
          variant="contained"
          fullWidth
          sx={{ mt: 1 }}
          onClick={onBuild}
          disabled={disableBuild}
        >
          {getText('BUILD')}
        </Button>
      </Box>
      {emptyStatisticsData && (
        <Typography
          padding={3}
          fontWeight="500"
          fontSize="1rem"
          color={(theme) => theme.palette.grey[700]}
          textAlign="center"
        >
          {getText('EMPTY_STATISTICS_DATA')}
        </Typography>
      )}

      <InfoModal
        centeredText
        isOpen={openInfo}
        handleClose={() => {
          setOpenInfo(false);
          dispatch(setStatisticsErrorMsgAction(''));
        }}
        modalText={textInfo}
      />
      <Effects tabType={tabType} requirements={requirements} />
    </>
  );
};
